import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import {
  Brain, Dumbbell, Heart, Sparkles, ArrowRight, Mail,
  Moon, Gamepad2, Globe, ShieldCheck, Headphones, Activity,
  ClipboardCheck, TrendingUp, RefreshCw, Shield
} from "lucide-react";
import LanguageSwitcher from "../components/LanguageSwitcher";
import DarkModeToggle from "../components/DarkModeToggle";

// A/B test — three portrait cards. Each visitor sees a random pair,
// locked to their session via localStorage. Variant is reported to Meta
// Pixel as a custom parameter so we can compare CTR/conversion by pair.
const PEOPLE_IMAGES = {
  nature: {
    src: "https://customer-assets.emergentagent.com/job_90f69793-09a6-4002-b9ba-1bb9df71fdef/artifacts/k11602ij_1776429336939.png",
    alt: "Woman using LumiFit in nature",
    quote: "My morning check-in takes 30 seconds. The AI plan keeps me grounded all day.",
  },
  office: {
    src: "https://customer-assets.emergentagent.com/job_90f69793-09a6-4002-b9ba-1bb9df71fdef/artifacts/wwi6sh35_1776430068472.png",
    alt: "Professional using LumiFit at work",
    quote: "Between meetings, I do a 3-minute focus meditation. Game changer for my afternoon energy.",
  },
  meditation: {
    src: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=1200&auto=format&fit=crop&q=80",
    alt: "Person meditating in a bright, sunlit room",
    quote: "Ten minutes of guided calm in the morning changes the whole day.",
  },
};

const AB_VARIANTS = {
  A: ["nature", "office"],       // control — original pair
  B: ["nature", "meditation"],   // meditation replaces office
  C: ["office", "meditation"],   // meditation replaces nature
};

const getLandingVariant = () => {
  try {
    let v = localStorage.getItem("landing_ab_variant");
    if (!v || !AB_VARIANTS[v]) {
      const keys = Object.keys(AB_VARIANTS);
      v = keys[Math.floor(Math.random() * keys.length)];
      localStorage.setItem("landing_ab_variant", v);
    }
    return v;
  } catch {
    return "A";
  }
};

const Landing = () => {
  const { t } = useTranslation();
  const [abVariant, setAbVariant] = useState("A");

  useEffect(() => {
    const v = getLandingVariant();
    setAbVariant(v);
    if (window.fbq) {
      window.fbq('track', 'ViewContent', {
        content_name: 'landing_page',
        content_category: `landing_variant_${v}`,
      });
    }
  }, []);

  const steps = [
    { step: "01", titleKey: "landing.howItWorks.step1.title", descKey: "landing.howItWorks.step1.description" },
    { step: "02", titleKey: "landing.howItWorks.step2.title", descKey: "landing.howItWorks.step2.description" },
    { step: "03", titleKey: "landing.howItWorks.step3.title", descKey: "landing.howItWorks.step3.description" },
    { step: "04", titleKey: "landing.howItWorks.step4.title", descKey: "landing.howItWorks.step4.description" }
  ];

  const highlights = [
    { key: "aiPlans", icon: Activity, color: "from-cyan-400 to-sky-500", bg: "bg-cyan-400/15" },
    { key: "meditation", icon: Headphones, color: "from-sky-400 to-indigo-500", bg: "bg-sky-400/15" },
    { key: "sleepStories", icon: Moon, color: "from-indigo-400 to-violet-500", bg: "bg-indigo-400/15" },
    { key: "brainGames", icon: Gamepad2, color: "from-violet-400 to-purple-500", bg: "bg-violet-400/15" },
    { key: "languages", icon: Globe, color: "from-teal-400 to-emerald-500", bg: "bg-teal-400/15" },
    { key: "privacy", icon: ShieldCheck, color: "from-slate-400 to-zinc-500", bg: "bg-slate-400/15" },
  ];

  return (
    <div className="min-h-screen bg-sky-50 dark:bg-slate-900 relative overflow-hidden">
      {/* Background gradient orbs */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-sky-300/30 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-indigo-300/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 right-0 w-72 h-72 bg-cyan-300/20 rounded-full blur-3xl pointer-events-none" />

      {/* Header */}
      <header className="fixed top-0 w-full z-50 glass-header">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 sm:py-4 flex justify-between items-center">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => window.location.href = '/'} data-testid="app-logo-home">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-sky-400 to-indigo-400 flex items-center justify-center glow-primary">
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-white" strokeWidth={1.5} />
            </div>
            <span className="text-lg sm:text-xl font-light text-sky-900 dark:text-sky-100" style={{ fontFamily: 'Outfit' }}>
              {t('common.appName')}
            </span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <DarkModeToggle />
            <LanguageSwitcher />
            <Button 
              onClick={() => window.location.href = '/auth'}
              variant="outline"
              className="btn-secondary-glass hidden sm:inline-flex mr-1"
              data-testid="header-signin-btn"
            >
              {t('auth.login', 'Sign In')}
            </Button>
            <Button 
              onClick={() => window.location.href = '/auth'}
              className="glow-button text-xs sm:text-sm px-3 sm:px-4"
              data-testid="header-login-btn"
            >
              <span className="hidden sm:inline">{t('landing.hero.cta')}</span>
              <span className="sm:hidden">{t('auth.login', 'Sign In')}</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main className="relative z-10 pt-24 sm:pt-28 pb-16 sm:pb-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Real People — First thing you see. Human faces, not a headline. */}
          <div className="mb-14 sm:mb-20" data-testid="real-people-section">
            <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-10">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sky-500 mb-3">Real People</p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-light tracking-tight text-sky-900 dark:text-sky-100" style={{ fontFamily: 'Outfit' }}>
                Built for people who like to feel good
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 max-w-5xl mx-auto" data-testid={`ab-variant-${abVariant}`}>
              {AB_VARIANTS[abVariant].map((imgKey) => {
                const img = PEOPLE_IMAGES[imgKey];
                return (
                  <figure key={imgKey} className="relative rounded-3xl overflow-hidden shadow-xl group" data-testid={`people-card-${imgKey}`}>
                    <img
                      src={img.src}
                      alt={img.alt}
                      className="w-full h-64 sm:h-80 md:h-96 object-cover group-hover:scale-[1.02] transition-transform duration-700"
                      loading="eager"
                      fetchPriority="high"
                    />
                    <figcaption className="absolute inset-x-0 bottom-0 p-4 sm:p-5 bg-gradient-to-t from-black/70 via-black/40 to-transparent">
                      <p className="text-sm sm:text-base text-white/95 leading-snug italic">&ldquo;{img.quote}&rdquo;</p>
                    </figcaption>
                  </figure>
                );
              })}
            </div>
          </div>

          <div className="text-center max-w-4xl mx-auto">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-500 mb-3 sm:mb-4">
              {t('common.tagline')}
            </p>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-light tracking-tight text-sky-900 dark:text-sky-100 mb-4 sm:mb-6" style={{ fontFamily: 'Outfit' }}>
              {t('landing.hero.title1')}
              <br />
              <span className="text-sky-500">{t('landing.hero.title2')}</span>
            </h1>
            <p className="text-sm sm:text-lg text-slate-600 dark:text-slate-400 leading-relaxed max-w-2xl mx-auto mb-8 sm:mb-10">
              {t('landing.hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-2 sm:px-0">
              <Button 
                onClick={() => { if (window.fbq) window.fbq('track', 'Lead', { content_name: 'hero_cta' }); window.location.href = '/auth'; }}
                className="glow-button text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6"
                data-testid="hero-get-started-btn"
              >
                {t('landing.hero.cta')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                variant="outline"
                className="btn-secondary-glass text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6"
                data-testid="learn-more-btn"
                onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
              >
                {t('landing.hero.learnMore')}
              </Button>
            </div>
          </div>

          {/* 3 Core Pillar Cards */}
          <div id="features" className="mt-20 sm:mt-32 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            <div className="glass-card glass-card-hover p-5 sm:p-6 md:p-8" data-testid="physical-feature-card">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-cyan-400/20 flex items-center justify-center mb-4 sm:mb-6">
                <Dumbbell className="w-6 h-6 sm:w-7 sm:h-7 text-cyan-500" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg sm:text-xl font-normal text-sky-800 dark:text-sky-200 mb-2 sm:mb-3" style={{ fontFamily: 'Outfit' }}>
                {t('landing.features.physical.title')}
              </h3>
              <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 leading-relaxed">
                {t('landing.features.physical.description')}
              </p>
            </div>

            <div className="glass-card glass-card-hover p-5 sm:p-6 md:p-8" data-testid="cognitive-feature-card">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-indigo-300/30 flex items-center justify-center mb-4 sm:mb-6">
                <Brain className="w-6 h-6 sm:w-7 sm:h-7 text-indigo-500" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg sm:text-xl font-normal text-sky-800 dark:text-sky-200 mb-2 sm:mb-3" style={{ fontFamily: 'Outfit' }}>
                {t('landing.features.cognitive.title')}
              </h3>
              <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 leading-relaxed">
                {t('landing.features.cognitive.description')}
              </p>
            </div>

            <div className="glass-card glass-card-hover p-5 sm:p-6 md:p-8" data-testid="resilience-feature-card">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-sky-400/20 flex items-center justify-center mb-4 sm:mb-6">
                <Heart className="w-6 h-6 sm:w-7 sm:h-7 text-sky-500" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg sm:text-xl font-normal text-sky-800 dark:text-sky-200 mb-2 sm:mb-3" style={{ fontFamily: 'Outfit' }}>
                {t('landing.features.resilience.title')}
              </h3>
              <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 leading-relaxed">
                {t('landing.features.resilience.description')}
              </p>
            </div>
          </div>

          {/* Feature Highlights Grid — New features showcase */}
          <div className="mt-20 sm:mt-32 text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-500 mb-3 sm:mb-4">
              {t('landing.highlights.label', 'Everything You Need')}
            </p>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-light tracking-tight text-sky-900 dark:text-sky-100 mb-10 sm:mb-14" style={{ fontFamily: 'Outfit' }}>
              {t('landing.highlights.title', 'One App. Complete Wellness.')}
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-5">
              {highlights.map(({ key, icon: Icon, color, bg }) => (
                <div
                  key={key}
                  className="group relative p-4 sm:p-6 rounded-2xl bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm border border-white/50 dark:border-slate-700/50 hover:border-sky-200 dark:hover:border-sky-700 transition-all hover:shadow-lg hover:-translate-y-0.5"
                  data-testid={`highlight-${key}`}
                >
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl ${bg} flex items-center justify-center mx-auto mb-3 sm:mb-4 group-hover:scale-110 transition-transform`}>
                    <Icon className={`w-5 h-5 sm:w-6 sm:h-6 bg-gradient-to-r ${color} bg-clip-text`} strokeWidth={1.5} style={{ color: 'inherit' }} />
                  </div>
                  <h4 className="text-sm sm:text-base font-medium text-sky-800 dark:text-sky-200 mb-1 sm:mb-2" style={{ fontFamily: 'Outfit' }}>
                    {t(`landing.highlights.${key}.title`)}
                  </h4>
                  <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                    {t(`landing.highlights.${key}.description`)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* The Daily Loop — Show the cycle, not a feature list */}
          <div className="mt-20 sm:mt-32 text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-500 mb-3 sm:mb-4">
              {t('landing.loop.label', 'The Daily Loop')}
            </p>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-light tracking-tight text-sky-900 dark:text-sky-100 mb-10 sm:mb-16" style={{ fontFamily: 'Outfit' }}>
              {t('landing.loop.title', '30 Seconds to a Better Day')}
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 max-w-5xl mx-auto">
              {[
                { step: "1", icon: ClipboardCheck, color: "from-sky-400 to-cyan-400", bg: "bg-sky-400/10", key: "step1" },
                { step: "2", icon: TrendingUp, color: "from-emerald-400 to-teal-400", bg: "bg-emerald-400/10", key: "step2" },
                { step: "3", icon: Dumbbell, color: "from-indigo-400 to-violet-400", bg: "bg-indigo-400/10", key: "step3" },
                { step: "4", icon: Sparkles, color: "from-rose-400 to-pink-400", bg: "bg-rose-400/10", key: "step4" },
              ].map(({ step, icon: Icon, color, bg, key }) => (
                <div key={key} className="relative group" data-testid={`loop-${key}`}>
                  {/* Connector arrow (hidden on last item and mobile) */}
                  {step !== "4" && (
                    <div className="hidden lg:block absolute top-12 -right-3 w-6 text-sky-200 dark:text-sky-800 z-10">
                      <ArrowRight className="w-5 h-5" strokeWidth={1.5} />
                    </div>
                  )}
                  <div className="glass-card p-5 sm:p-6 h-full text-center group-hover:shadow-lg transition-all group-hover:-translate-y-0.5">
                    <div className={`w-14 h-14 rounded-2xl ${bg} flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                      <Icon className="w-7 h-7 text-sky-600 dark:text-sky-400" strokeWidth={1.5} />
                    </div>
                    <div className="text-xs font-semibold text-sky-400 mb-1">Step {step}</div>
                    <h4 className="text-sm sm:text-base font-medium text-sky-800 dark:text-sky-200 mb-2" style={{ fontFamily: 'Outfit' }}>
                      {t(`landing.loop.${key}.title`)}
                    </h4>
                    <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                      {t(`landing.loop.${key}.description`)}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Loop visual — circular arrow hint */}
            <div className="mt-8 flex items-center justify-center gap-2 text-sky-300 dark:text-sky-700">
              <div className="h-px w-12 bg-sky-200 dark:bg-sky-800" />
              <RefreshCw className="w-5 h-5" strokeWidth={1.5} />
              <span className="text-xs text-slate-400">Repeat daily</span>
              <div className="h-px w-12 bg-sky-200 dark:bg-sky-800" />
            </div>
          </div>

          {/* Trust Badge */}
          <div className="mt-12 sm:mt-16 text-center">
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/30" data-testid="trust-badge">
              <Shield className="w-4 h-4 text-emerald-500" strokeWidth={2} />
              <span className="text-xs text-emerald-700 dark:text-emerald-400">{t('landing.trustBadge', 'Your data is encrypted at rest. We never sell or share it.')}</span>
            </div>
          </div>

          {/* CTA Section — Gray-to-blue gradient with light blue box */}
          <div
            className="mt-20 sm:mt-32 rounded-3xl overflow-hidden"
            style={{
              background: 'linear-gradient(to bottom, #475569, #64748b, #94a3b8, #bae6fd, #e0f2fe, #f0f9ff, #ffffff)',
            }}
            data-testid="cta-section"
          >
            <div className="p-6 sm:p-8 md:p-12">
              <div
                className="rounded-2xl p-6 sm:p-8 md:p-10 text-center"
                style={{
                  background: 'linear-gradient(to bottom, #dbeafe, #eff6ff, #f8fbff, #ffffff)',
                }}
              >
                <h2 className="text-xl sm:text-2xl md:text-3xl font-light tracking-tight text-sky-900 mb-3 sm:mb-4" style={{ fontFamily: 'Outfit' }}>
                  {t('landing.cta.title')}
                </h2>
                <p className="text-sm sm:text-base text-slate-600 mb-6 sm:mb-8 max-w-xl mx-auto">
                  {t('landing.cta.subtitle')}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button 
                    onClick={() => window.location.href = '/auth'}
                    className="glow-button text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6"
                    data-testid="cta-get-started-btn"
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    {t('landing.cta.button')}
                  </Button>
                  <Button
                    onClick={() => window.location.href = '/auth'}
                    variant="outline"
                    className="btn-secondary-glass text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 border-sky-300 text-sky-700 hover:bg-sky-50"
                    data-testid="cta-email-signin-btn"
                  >
                    <Mail className="w-5 h-5 mr-2" />
                    {t('auth.login', 'Sign In')} with Email
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-sky-100 dark:border-slate-700">
        <div className="max-w-7xl mx-auto text-center text-sm text-slate-500 dark:text-slate-400 space-y-2">
          <p>&copy; 2026 {t('common.appName')}. {t('landing.footer')}</p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <a href="/blog" className="text-sky-500 hover:text-sky-700 transition-colors">
              Science Blog
            </a>
            <span className="text-slate-300 dark:text-slate-600">|</span>
            <a href="/privacy" className="text-sky-500 hover:text-sky-700 transition-colors">
              Privacy Policy & Terms
            </a>
            <span className="text-slate-300 dark:text-slate-600">|</span>
            <a href="/about" className="text-sky-500 hover:text-sky-700 transition-colors" data-testid="about-link">
              About
            </a>
            <span className="text-slate-300 dark:text-slate-600">|</span>
            <a href="mailto:info@lumifit.me" className="text-sky-500 hover:text-sky-700 transition-colors" data-testid="contact-email">
              info@lumifit.me
            </a>
            <span className="text-slate-300 dark:text-slate-600">|</span>
            <a href="/contact" className="text-sky-500 hover:text-sky-700 transition-colors inline-flex items-center gap-1" data-testid="contact-form-link">
              <Mail className="w-3.5 h-3.5" /> Contact Us
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
